import Row, { AutoRow } from 'components/Layout/Row'
import { CurrencyLogo } from 'components/Logo'
import { ModalActions } from 'components/Modal'
import tokens from 'config/constants/tokens'
import { useTranslation } from 'contexts/Localization'
import useTheme from 'hooks/useTheme'
import useToast from 'hooks/useToast'
import React, { useState } from 'react'
import { useTransactionAdder } from 'state/transactions/hooks'
import { useUserVvsBoost } from 'state/xVvs/hooks'
import { BIG_ZERO } from 'utils/bigNumber'
import { formatNumber, getBalanceAmount } from 'utils/formatBalance'
import useVvsBoostHandlers from 'views/xVvs/hooks/useVvsBoostHandlers'
import { Modal, Text, Button, AutoRenewIcon, InjectedModalProps, Box } from 'vvs-uikit'

interface WithdrawModalProps extends InjectedModalProps {
  stakeIds: number[]
}

const WithdrawalModal: React.FC<WithdrawModalProps> = ({ onDismiss, stakeIds }) => {
  const { t } = useTranslation()
  const { theme, isDark } = useTheme()

  const [pendingTx, setPendingTx] = useState<boolean>()

  const { boostData } = useUserVvsBoost()

  const { toastError } = useToast()

  const { onBatchWithdraw } = useVvsBoostHandlers()
  const amount = getBalanceAmount(
    boostData.stakes.map((x, i) => (stakeIds.includes(i) ? x.amount : BIG_ZERO)).reduce((a, b) => a.plus(b), BIG_ZERO),
    tokens.xvvs.decimals,
  )

  const addTransaction = useTransactionAdder()
  const onConfirm = async () => {
    setPendingTx(true)
    try {
      const response = await onBatchWithdraw(stakeIds)
      addTransaction(response, {
        title: t('Withdraw Transaction completed'),
        summary: t('%value% %symbol% have been removed from the vault', {
          value: formatNumber(Number(amount), 2, 6),
          symbol: tokens.xvvs.symbol,
        }),
      })
    } catch (e: any) {
      console.error(e)
      toastError(t('Withdraw Transaction failed'), t('xVVS removal failed, please retry later'))
    } finally {
      setPendingTx(false)
      onDismiss()
    }
  }

  return (
    <Modal title={t('Confirm Withdrawal')} bodyPadding="0 24px" onDismiss={onDismiss}>
      <Box>
        <Text small mb={2}>
          {t('Withdraw amount')}
        </Text>
        <Row justify="space-between">
          <AutoRow>
            <CurrencyLogo size="24px" currency={tokens.xvvs} />
            <Text fontSize="20px" ml="8px">
              {formatNumber(amount.toNumber(), 2, 6)}
            </Text>
          </AutoRow>
          <Text fontSize="20px">{tokens.xvvs.symbol}</Text>
        </Row>
        <Box
          maxWidth="468px"
          borderRadius="12px"
          background={isDark ? '#424B6E' : theme.colors.lightAqua}
          p="18px"
          mt={3}
        >
          <Text small>{t('You will stop earning the Vault VVS rewards by removing your unlocked xVVS.')}</Text>
        </Box>
        <ModalActions>
          <Button
            scale="lg"
            width="100%"
            onClick={onConfirm}
            isLoading={pendingTx}
            endIcon={pendingTx ? <AutoRenewIcon spin color="currentColor" /> : null}
          >
            {t('Confirm Withdraw')}
          </Button>
        </ModalActions>
      </Box>
    </Modal>
  )
}

export default WithdrawalModal
